import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useContent } from "@/content";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.3, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const c = useContent();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0" data-testid="hero-photo-frame">
        <motion.img
          src="/images/jeremiah-monument.webp"
          alt="Jeremiah Womack standing before the Washington Monument"
          data-testid="hero-photo"
          initial={{ scale: 1.15, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }}
          className="w-full h-full object-cover object-[center_20%]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#05070F] via-[#05070F]/55 to-[#05070F]/15" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#05070F]/70 via-transparent to-transparent" />
      </motion.div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute top-20 sm:top-24 left-5 sm:left-12 lg:left-20 z-10 font-meta text-[9px] sm:text-[10px] tracking-[0.3em] text-blue-100/70"
      >
        FIG. 00 — JEREMIAH WOMACK. WASHINGTON, D.C.
      </motion.p>

      <motion.div style={{ opacity: textOpacity }} className="relative z-10 w-full px-5 sm:px-12 lg:px-20 pt-40 pb-20 sm:pb-24">
        <MaskedLine delay={0.5}>
          <span className="font-meta text-[10px] sm:text-sm tracking-[0.3em] sm:tracking-[0.35em] text-blue-100/80" data-testid="hero-eyebrow">
            {c.hero_eyebrow || "A DIGITAL MANIFESTO — EST. 2025"}
          </span>
        </MaskedLine>

        <h1 className="font-display font-extrabold uppercase tracking-tighter leading-[0.88] w-full mt-4 sm:mt-5" data-testid="hero-title">
          <MaskedLine delay={0.7} className="pr-2 text-[clamp(1.6rem,9vw,11rem)]">JEREMIAH</MaskedLine>
          <MaskedLine delay={0.85} className="pr-2 text-[clamp(1.75rem,9.7vw,11.9rem)]">
            <span className="text-aura-gradient">WOMACK</span>
          </MaskedLine>
        </h1>

        <div className="mt-7 sm:mt-10 flex flex-col gap-5 sm:gap-7 max-w-3xl">
          <MaskedLine delay={1.1}>
            <p className="font-meta text-[10px] sm:text-sm tracking-[0.2em] sm:tracking-[0.25em] text-blue-100/80" data-testid="hero-subline">
              16 / CREATIVE THINKER / WRITER / SOCIAL COMMENTATOR / INTELLECTUAL OMNIVORE
            </p>
          </MaskedLine>
          <MaskedLine delay={1.25}>
            <p className="font-display text-lg sm:text-2xl lg:text-3xl font-bold uppercase tracking-tight text-white border-l-2 border-[#4F7CFF] pl-4 sm:pl-5" data-testid="hero-statement">
              {c.hero_statement || "The future is made in the present."}
            </p>
          </MaskedLine>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.1, duration: 1 }}
        className="absolute bottom-6 right-5 sm:right-12 lg:right-20 z-10 flex items-center gap-4"
      >
        <span className="font-meta text-[9px] sm:text-[10px] tracking-[0.3em] text-blue-100/60">SCROLL TO ENTER THE ARCHIVE</span>
        <span className="block w-12 sm:w-16 h-px bg-blue-100/40" />
      </motion.div>
    </section>
  );
};
